import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';

import { crisisPlanActions, treatmentPlanActions } from '../../../../../store';
import { crisisPlanSchema } from '../../crisis-plan/validationSchema';
import { getAllInvalidFieldNames } from '../../../../../helpers';
import { TreatmentPlanPages } from '../../constants/general';

import type { RootState } from '../../../../../store';
import type { ICrisisPlan } from '../../../../../utils/types';

function useValidateCrisisPlan() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { current: treatmentPlan } = useSelector((state: RootState) => state.treatmentPlans);
  const { selected: selectedClient } = useSelector((state: RootState) => state.clients);

  const { treatmentPlanId: currentTPId, current: crisisPlanData } = useSelector(
    (state: RootState) => state.crisisPlans,
  );

  const [isValid, setIsValid] = useState<boolean>(false);

  useEffect(() => {
    if (!treatmentPlan?.id || !selectedClient?.id) return;
    let existingCrisisPlanData = crisisPlanData;
    if (currentTPId !== treatmentPlan.id) {
      dispatch(crisisPlanActions.updateState({ key: 'crisisPlanData', data: null }));
      existingCrisisPlanData = null;
    }
    if (currentTPId === treatmentPlan?.id || existingCrisisPlanData) {
      const data =
        existingCrisisPlanData?.id || existingCrisisPlanData?.is_needed !== undefined
          ? existingCrisisPlanData
          : null;
      fetchAndValidateData(data);
    } else {
      fetchAndValidateData(null);
    }
    dispatch(crisisPlanActions.updateState({ key: 'treatmentPlanId', data: treatmentPlan?.id }));
  }, [treatmentPlan?.id, selectedClient]);

  const fetchAndValidateData = async (crisisPlanData: ICrisisPlan | null) => {
    let data = crisisPlanData;
    if (!crisisPlanData?.id && crisisPlanData?.is_needed === undefined) {
      const response = await dispatch(
        crisisPlanActions.getCrisisPlan({
          treatmentPlanId: treatmentPlan?.id,
          getAccessTokenSilently,
        }),
      );
      data = response.payload;
    }
    validate(data);
  };

  function validate(data: ICrisisPlan | null) {
    let errorCount = 0;
    if (!data?.id && data?.is_needed === undefined) {
      setIsValid(false);
      dispatch(
        treatmentPlanActions.updateBadge({
          [TreatmentPlanPages.CrisisPlan]: '!',
        }),
      );
    } else if (data.is_needed) {
      const errorObject = getAllInvalidFieldNames(crisisPlanSchema, data);
      errorCount = Object.keys(errorObject).length;
      setIsValid(errorCount === 0 ? true : false);
      dispatch(
        treatmentPlanActions.updateBadge({
          [TreatmentPlanPages.CrisisPlan]: errorCount,
        }),
      );
    } else if (data.is_needed === false) {
      setIsValid(true);
      dispatch(
        treatmentPlanActions.updateBadge({
          [TreatmentPlanPages.CrisisPlan]: 0,
        }),
      );
    }
  }

  return isValid;
}

export default useValidateCrisisPlan;
